import React, { useState, useEffect } from 'react'
import { Link } from 'react-router-dom'
import { Table, Button } from 'reactstrap'
import { ToastNotification } from '../Lessons/CourseReport/CourseReportComponents'
import { addData, updateData, getStudentIdData as getStudentsGrade } from '../../lib/gradeinmonths'
import { getStudentIdWithClassData as getStudentLessons } from '../../lib/studentlessonsview'
import { getIdData as getStudentInfo } from '../../lib/studentinfo'
import { ClassForLessonSelect } from '../Common/FormSelects'
import { useRoleBasedUrl } from '../../Hooks/useRoleBasedUrl'
import { useLayout } from '../../Hooks/useLayout'
import LessonForGradeModal from './LessonForGradeModal'

const GradeInMonth = ({ studentId, classId, isStudent, isProfile = false }) => {
	const [sClass, setSClass] = useState(null)
	const [studentLessons, setStudentLessons] = useState([])
	const [grade, setGrade] = useState([])
	const [toastMessage, setToastMessage] = useState('')
	const [showToast, setShowToast] = useState(false)
	const [loading, setLoading] = useState(true)
	const [submitting, setSubmitting] = useState(false)
	const [isModalOpen, setIsModalOpen] = useState(false)
	const role = useRoleBasedUrl()
	const layout = useLayout()

	const monthList = ['September', 'October', 'November', 'December', 'January', 'February', 'March', 'April', 'May', 'June']

	const showToastMessage = (message) => {
		setToastMessage(message)
		setShowToast(true)
		setTimeout(() => setShowToast(false), 3000)
	}

	async function getParameterData() {
		try {
			setLoading(true)

			// Determine the class to use
			let effectiveClass = sClass || classId

			// If we still don't have a class, fetch student data
			if (!effectiveClass) {
				const studentData = await getStudentInfo(studentId)
				if (studentData && studentData.length > 0) {
					effectiveClass = studentData[0].class || 9
					setSClass(effectiveClass)
				} else {
					effectiveClass = 9 // Default fallback
					setSClass(effectiveClass)
				}
			}

			const gradeData = await getStudentsGrade(studentId)
			setGrade(gradeData)

			const stLessons = await getStudentLessons(studentId, effectiveClass)
			setStudentLessons(stLessons)
		} catch (error) {
			console.error('Error fetching grade data:', error)
		} finally {
			setLoading(false)
		}
	}

	function getGrade(les, month) {
		if (grade.length > 0) {
			const result = grade.filter((item) => item.lesson === les && item.month === month)
			if (result.length > 0) return result[0].grade
			else return 0
		} else return 0
	}

	function getRecordId(month, les) {
		const result = grade.filter((item) => item.lesson === parseInt(les) && item.month === month)
		if (result.length > 0) return result[0].id
		else return 0
	}

	async function addGradeData(month, lesson, gradeValue) {
		await addData({
			studentId: studentId,
			month: month,
			lesson: parseInt(lesson, 10),
			grade: parseInt(gradeValue, 10) || 0
		})
	}

	async function updateGradeData(recId, gradeValue) {
		await updateData({
			id: recId,
			grade: parseInt(gradeValue, 10) || 0
		})
	}

	function getGradeColor(lesson, month) {
		const gradeValue = getGrade(lesson, month)
		let color = '#FFFFFF'
		if (gradeValue > 0 && gradeValue < 65) color = '#FF0000'
		if (gradeValue > 64 && gradeValue < 71) color = '#FFA500'
		if (gradeValue > 70 && gradeValue < 81) color = '#FFFF00'
		if (gradeValue > 80 && gradeValue < 91) color = '#32CD32'
		if (gradeValue > 90) color = '#1E90FF'

		return { backgroundColor: color }
	}

	const handleSubmit = async (e) => {
		e.preventDefault()
		setSubmitting(true)

		try {
			const form = e.target
			const formData = new FormData(form)
			const formEl = [...formData.entries()]

			// Create an array of promises for parallel execution
			const updatePromises = formEl
				.filter(([name, value]) => {
					// Only process non-empty values
					return value && value !== '' && value !== '0'
				})
				.map((entry) => {
					const [name, value] = entry
					const monthLesson = name.split('-')
					const month = monthLesson[0]
					const lesson = monthLesson[1]
					let recId = getRecordId(month, lesson)

					if (recId > 0) {
						return updateGradeData(recId, value)
					} else {
						return addGradeData(month, lesson, value)
					}
				})

			// Execute all updates in parallel
			if (updatePromises.length > 0) {
				await Promise.all(updatePromises)
			}

			showToastMessage('Grades updated successfully!')
			window.scrollTo(0, 0)
			await getParameterData()
		} catch (error) {
			console.error('Error updating grades:', error)
		} finally {
			setSubmitting(false)
		}
	}

	const toggleModal = () => {
		setIsModalOpen(!isModalOpen)
	}

	const handleSaveCourse = (message) => {
		showToastMessage(message)
		getParameterData()
	}

	// Initialize class from prop
	useEffect(() => {
		if (classId && !sClass) {
			setSClass(classId)
		}
	}, [classId])

	// Fetch data when studentId is available
	useEffect(() => {
		if (studentId) {
			getParameterData()
		}
	}, [studentId])

	// Re-fetch when instructor changes the class selector
	useEffect(() => {
		if (sClass && studentId && !loading) {
			getParameterData()
		}
	}, [sClass])

	return (
		<div className="card mb-4 shadow-sm" style={{ borderRadius: '8px' }}>
			<ToastNotification show={showToast} message={toastMessage} onClose={() => setShowToast(false)} />
			<div className="card-header border-bottom d-flex justify-content-between align-items-center">
				<div className="d-flex align-items-center">
					{!isProfile && (
						<button
							type="button"
							className="btn btn-link p-0 me-2"
							data-bs-toggle="collapse"
							data-bs-target="#collapseGrades"
							aria-expanded="false"
							aria-controls="collapseGrades"
						>
							<i className="fa fa-chevron-down"></i>
						</button>
					)}
					<h5 className="mb-0 d-inline-block">{isProfile ? 'Current Grades' : 'Update Grades'}</h5>
				</div>
			</div>
			<div className={isProfile ? '' : 'collapse'} id="collapseGrades">
				<div className="card-body">
					{!isProfile && (
						<div className="d-flex pb-3 justify-content-between align-items-center">
							<div className="d-flex align-items-center gap-3">
								<div className="d-flex align-items-center">
									<h6 className="mb-0 me-3">Class:</h6>
									{isStudent ? (
										<span className="fw-bold">{classId}</span>
									) : (
										<div style={{ minWidth: '150px' }}>
											<ClassForLessonSelect value={sClass} setValue={setSClass} />
										</div>
									)}
								</div>
							</div>
							<div className="d-flex align-items-center gap-2">
								<button className="btn btn-primary btn-sm" onClick={toggleModal}>
									<i className="fa fa-plus me-2"></i>
									Add courses
								</button>
								<Link
									to={
										isStudent
											? `${process.env.PUBLIC_URL}/${role}/student-lessonsforgrade/${layout}`
											: `${process.env.PUBLIC_URL}/${role}/instructor-studentlessonforgrade/${studentId}/${layout}`
									}
									className="btn btn-info btn-sm"
								>
									<i className="fa fa-list me-2"></i>
									List of Courses
								</Link>
							</div>
						</div>
					)}


					{loading ? (
						<div className="text-center">
							<div className="spinner-border" role="status">
								<span className="visually-hidden">Loading...</span>
							</div>
							<p className="mt-2">Loading grades...</p>
						</div>
					) : isProfile ? (
						<div className="table-responsive">
							<Table hover bordered className="mb-0">
								<thead>
									<tr>
										<th style={{ width: '200px' }}>Course</th>
										{monthList.map((month, ix) => (
											<th key={ix} className="text-center" style={{ minWidth: '80px' }}>
												{month.substring(0, 3)}
											</th>
										))}
									</tr>
								</thead>
								<tbody>
									{studentLessons.length > 0 ? (
										studentLessons.map((less) => (
											<tr key={less.id}>
												<td className="fw-bold">
													{less.pname}
													<br />
													<span className="fw-normal text-muted">{less.lessonName}</span>
												</td>
												{monthList.map((month, ix) => {
													const currentGrade = getGrade(less.id, month)
													return (
														<td key={ix} className="text-center" style={getGradeColor(less.id, month)}>
															{currentGrade || '-'}
														</td>
													)
												})}
											</tr>
										))
									) : (
										<tr>
											<td colSpan={monthList.length + 1} className="text-center">
												No courses found.
											</td>
										</tr>
									)}
								</tbody>
							</Table>
						</div>
					) : (
						<form method="post" onSubmit={handleSubmit}>
							<div className="table-responsive">
								<Table hover bordered className="mb-0">
									<thead>
										<tr>
											<th style={{ width: '200px' }}>Course</th>
											{monthList.map((month, ix) => (
												<th key={ix} className="text-center" style={{ minWidth: '80px' }}>
													{month.substring(0, 3)}
												</th>
											))}
										</tr>
									</thead>
									<tbody>
										{studentLessons.length > 0 ? (
											studentLessons.map((less) => (
												<tr key={less.id}>
													<td className="fw-bold">
														{less.pname}
														<br />
														<span className="fw-normal text-muted">{less.lessonName}</span>
													</td>
													{monthList.map((month, ix) => {
														const currentGrade = getGrade(less.id, month)
														return (
															<td key={ix} className="p-1">
																<input
																	type="number"
																	value={currentGrade || ''}
																	name={month + '-' + less.id}
																	min="0"
																	max="100"
																	className="form-control form-control-sm text-center"
																	placeholder="-"
																	style={getGradeColor(less.id, month)}
																	onChange={(e) => {
																		const newGradeValue = parseInt(e.target.value, 10) || 0
																		const updatedGrade = grade.map((item) =>
																			item.lesson === less.id && item.month === month
																				? {
																						...item,
																						grade: newGradeValue
																				  }
																				: item
																		)
																		if (!updatedGrade.find((item) => item.lesson === less.id && item.month === month)) {
																			updatedGrade.push({
																				studentId,
																				lesson: less.id,
																				month,
																				grade: newGradeValue
																			})
																		}
																		setGrade(updatedGrade)
																	}}
																/>
															</td>
														)
													})}
												</tr>
											))
										) : (
											<tr>
												<td colSpan={monthList.length + 1} className="text-center">
													No courses found. Please add courses from the "List of Courses" page.
												</td>
											</tr>
										)}
									</tbody>
								</Table>
							</div>
							<div className="d-flex justify-content-between mt-3">
								<div></div>
								<Button type="submit" color="primary" disabled={submitting}>
									<i className="fa fa-save me-2"></i>
									{submitting ? 'Updating...' : 'Update Grades'}
								</Button>
							</div>
						</form>
					)}
				</div>
			</div>
			<LessonForGradeModal isOpen={isModalOpen} toggle={toggleModal} studentId={studentId} lessonToEdit={null} onSave={handleSaveCourse} />
		</div>
	)
}

export { GradeInMonth }
